(() => {
  if (window.__UGBDeliveryDateEngineLoaded) return;
  window.__UGBDeliveryDateEngineLoaded = true;

  const ATTRIBUTE_NAME = 'Estimated Shipping Date:';
  const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];
  const SHORT_MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  let globalConfigCache;

  const pad = (value) => String(value).padStart(2, '0');
  const toIso = (date) => `${date.getUTCFullYear()}-${pad(date.getUTCMonth() + 1)}-${pad(date.getUTCDate())}`;
  const parseIso = (value) => {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(value || '')) return null;
    const [year, month, day] = value.split('-').map(Number);
    const date = new Date(Date.UTC(year, month - 1, day));
    return Number.isNaN(date.getTime()) ? null : date;
  };
  const addDays = (date, days) => new Date(date.getTime() + days * 86400000);
  const todayUtc = () => {
    const now = new Date();
    return new Date(Date.UTC(now.getFullYear(), now.getMonth(), now.getDate()));
  };
  const isoWeekday = (date) => {
    const day = date.getUTCDay();
    return day === 0 ? 7 : day;
  };
  const longDate = (date) => `${MONTHS[date.getUTCMonth()]} ${date.getUTCDate()}, ${date.getUTCFullYear()}`;
  const shortDate = (date) => `${SHORT_MONTHS[date.getUTCMonth()]} ${date.getUTCDate()}, ${date.getUTCFullYear()}`;

  function readGlobalConfig() {
    if (globalConfigCache !== undefined) return globalConfigCache;
    const node = document.getElementById('UGB-Delivery-Config');
    if (!node) return null;
    try {
      globalConfigCache = JSON.parse(node.textContent || '{}');
    } catch (error) {
      console.warn('[UGB delivery] Invalid Theme Editor config', error);
      globalConfigCache = null;
    }
    return globalConfigCache;
  }

  function parseLegacyDate(value) {
    if (!value) return null;
    const iso = parseIso(value);
    if (iso) return iso;
    const match = String(value).trim().match(/^([A-Za-z]+)\s+(\d{1,2}),\s*(\d{4})$/);
    if (!match) return null;
    const month = MONTHS.findIndex((name) => name.toLowerCase() === match[1].toLowerCase());
    if (month < 0) return null;
    return new Date(Date.UTC(Number(match[3]), month, Number(match[2])));
  }

  function parseBlockedRanges(raw) {
    return String(raw || '')
      .split(/\r?\n/)
      .map((line) => line.trim())
      .filter(Boolean)
      .map((line) => {
        const [startRaw, endRaw, ...reasonParts] = line.split('|').map((part) => part.trim());
        const start = parseIso(startRaw);
        const end = parseIso(endRaw || startRaw);
        if (!start || !end) return null;
        const first = start <= end ? start : end;
        const last = start <= end ? end : start;
        return { start: toIso(first), end: toIso(last), reason: reasonParts.join(' | ') };
      })
      .filter(Boolean);
  }

  function readConfig(root) {
    const globalDelivery = readGlobalConfig()?.delivery;
    const minDays = Math.max(0, Number(globalDelivery?.minDays ?? root.dataset.ugbMinDays ?? 3));
    const maxDays = Math.max(minDays, Number(globalDelivery?.maxDays ?? root.dataset.ugbMaxDays ?? 360));
    const weekdaysSource = Array.isArray(globalDelivery?.weekdays)
      ? globalDelivery.weekdays
      : String(root.dataset.ugbWeekdays || '1,2,3,4,5').split(',');
    const weekdays = new Set(
      weekdaysSource.map((value) => Number(String(value).trim())).filter((value) => value >= 1 && value <= 7)
    );

    let blockedRaw = globalDelivery?.blockedRanges;
    if (blockedRaw == null) {
      const blockedNode = root.querySelector('[data-ugb-blocked-ranges]');
      if (blockedNode) {
        try { blockedRaw = JSON.parse(blockedNode.textContent || '""'); } catch { blockedRaw = blockedNode.textContent || ''; }
      }
    }
    return { minDays, maxDays, weekdays, blocked: parseBlockedRanges(blockedRaw), copy: globalDelivery?.copy || {} };
  }

  function applyCopy(root, kind, config) {
    const copy = config.copy || {};
    if (kind === 'product') {
      const eyebrow = root.querySelector('.ugb-delivery-estimate__eyebrow');
      const line = root.querySelector('.ugb-delivery-estimate__line');
      const note = root.querySelector('.ugb-delivery-estimate__note');
      const dateNode = root.querySelector('[data-ugb-earliest-date]');
      if (eyebrow && copy.productEyebrow) eyebrow.textContent = copy.productEyebrow;
      if (line && dateNode && copy.productEarliestLabel) {
        line.childNodes.forEach((node) => { if (node !== dateNode && node.nodeType === Node.TEXT_NODE) node.textContent = ''; });
        line.insertBefore(document.createTextNode(`${copy.productEarliestLabel} `), dateNode);
      }
      if (note && copy.productNote) note.textContent = copy.productNote;
      return;
    }

    const eyebrow = root.querySelector('.ugb-delivery-card__eyebrow');
    const title = root.querySelector('.ugb-delivery-card__title');
    const helper = root.querySelector('.ugb-delivery-card__helper');
    const earliest = root.querySelector('.ugb-delivery-card__earliest');
    const dateNode = root.querySelector('[data-ugb-earliest-date]');
    const value = root.querySelector('[data-ugb-delivery-value]');
    if (eyebrow && copy.cartEyebrow) eyebrow.textContent = copy.cartEyebrow;
    if (title && copy.cartTitle) title.textContent = copy.cartTitle;
    if (helper && copy.cartHelper) helper.textContent = copy.cartHelper;
    if (earliest && dateNode && copy.cartEarliestLabel) {
      earliest.childNodes.forEach((node) => { if (node !== dateNode && node.nodeType === Node.TEXT_NODE) node.textContent = ''; });
      earliest.insertBefore(document.createTextNode(`${copy.cartEarliestLabel} `), dateNode);
    }
    if (value && !root.dataset.ugbCurrentDate && copy.cartPlaceholder) value.textContent = copy.cartPlaceholder;
  }

  function isBlocked(date, config) {
    const iso = toIso(date);
    return config.blocked.some((range) => iso >= range.start && iso <= range.end);
  }

  function isSelectable(date, config) {
    const today = todayUtc();
    const minimum = addDays(today, config.minDays);
    const maximum = addDays(today, config.maxDays);
    if (date < minimum || date > maximum) return false;
    if (!config.weekdays.has(isoWeekday(date))) return false;
    if (isBlocked(date, config)) return false;
    return true;
  }

  function earliestAvailable(config) {
    const maximum = addDays(todayUtc(), config.maxDays);
    let cursor = addDays(todayUtc(), config.minDays);
    while (cursor <= maximum && !isSelectable(cursor, config)) cursor = addDays(cursor, 1);
    return cursor <= maximum ? cursor : null;
  }

  function formatMonthLabel(date) {
    return `${MONTHS[date.getUTCMonth()]} ${date.getUTCFullYear()}`;
  }

  function renderCalendar(root, state) {
    const label = root.querySelector('[data-ugb-calendar-month-label]');
    const daysContainer = root.querySelector('[data-ugb-calendar-days]');
    if (!label || !daysContainer) return;

    const year = state.month.getUTCFullYear();
    const month = state.month.getUTCMonth();
    label.textContent = formatMonthLabel(state.month);
    daysContainer.innerHTML = '';

    const first = new Date(Date.UTC(year, month, 1));
    const offset = (isoWeekday(first) - 1 + 7) % 7;
    const gridStart = addDays(first, -offset);

    for (let index = 0; index < 42; index += 1) {
      const date = addDays(gridStart, index);
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'ugb-delivery-calendar__day';
      button.textContent = String(date.getUTCDate());
      button.dataset.date = toIso(date);
      button.setAttribute('aria-label', longDate(date));

      if (date.getUTCMonth() !== month) button.classList.add('is-outside');
      if (toIso(date) === state.selectedIso) button.classList.add('is-selected');
      if (!isSelectable(date, state.config)) {
        button.disabled = true;
        button.classList.add('is-disabled');
      }
      button.addEventListener('click', () => selectDate(root, state, date));
      daysContainer.appendChild(button);
    }

    const prev = root.querySelector('[data-ugb-calendar-prev]');
    const next = root.querySelector('[data-ugb-calendar-next]');
    const minMonth = earliestAvailable(state.config);
    const maxMonth = addDays(todayUtc(), state.config.maxDays);
    if (prev && minMonth) prev.disabled = year === minMonth.getUTCFullYear() && month <= minMonth.getUTCMonth();
    if (next) next.disabled = year === maxMonth.getUTCFullYear() && month >= maxMonth.getUTCMonth();
  }

  async function persistCartAttribute(value) {
    const response = await fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({ attributes: { [ATTRIBUTE_NAME]: value } })
    });
    if (!response.ok) throw new Error(`Cart update failed: ${response.status}`);
    return response.json();
  }

  function setError(root, message = '') {
    const error = root.querySelector('[data-ugb-delivery-error]');
    if (!error) return;
    error.textContent = message;
    error.hidden = !message;
    root.classList.toggle('has-error', Boolean(message));
  }

  async function selectDate(root, state, date) {
    if (!isSelectable(date, state.config)) return;
    state.selectedIso = toIso(date);
    const human = longDate(date);
    const buttonText = root.querySelector('[data-ugb-delivery-value]');
    const hidden = root.querySelector('[data-ugb-delivery-attribute]');
    if (buttonText) buttonText.textContent = human;
    if (hidden) hidden.value = human;
    setError(root, '');
    root.querySelector('[data-ugb-calendar]')?.setAttribute('hidden', '');
    root.querySelector('[data-ugb-delivery-trigger]')?.setAttribute('aria-expanded', 'false');
    renderCalendar(root, state);
    try {
      await persistCartAttribute(human);
      root.dataset.ugbSelectedIso = state.selectedIso;
    } catch (error) {
      console.error('[UGB delivery] Could not save preferred delivery date', error);
      setError(root, state.config.copy.saveError || 'We could not save the selected date. Please choose it again before checkout.');
    }
  }

  function getDigitalGiftDates(root) {
    const node = root.querySelector('[data-ugb-digital-dates]');
    if (!node) return [];
    try {
      const parsed = JSON.parse(node.textContent || '[]');
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }

  function validateCart(root, state) {
    if (!state.selectedIso) {
      setError(root, state.config.copy.requiredError || 'Please select a preferred delivery date before checkout.');
      root.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return false;
    }

    const selected = parseIso(state.selectedIso);
    if (!selected || !isSelectable(selected, state.config)) {
      setError(root, state.config.copy.unavailableError || 'The selected delivery date is no longer available. Please choose another date.');
      root.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return false;
    }

    const mismatches = getDigitalGiftDates(root).filter((item) => item.date && item.date > state.selectedIso);
    if (mismatches.length) {
      const names = mismatches.map((item) => item.title).filter(Boolean).join(', ');
      const base = state.config.copy.digitalAfterError || 'The email gift message cannot be scheduled after the physical preferred delivery date. Please edit the gift message date before checkout.';
      setError(root, `${base}${names ? ` (${names})` : ''}`);
      root.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return false;
    }

    setError(root, '');
    return true;
  }

  function initCart(root) {
    if (root.dataset.ugbDeliveryInitialized === 'true') return;
    root.dataset.ugbDeliveryInitialized = 'true';

    const config = readConfig(root);
    applyCopy(root, 'cart', config);
    const earliest = earliestAvailable(config);
    const earliestLabel = root.querySelector('[data-ugb-earliest-date]');
    if (earliestLabel && earliest) earliestLabel.textContent = shortDate(earliest);

    let selected = parseLegacyDate(root.dataset.ugbCurrentDate || '');
    if (selected && !isSelectable(selected, config)) selected = null;
    const state = { config, selectedIso: selected ? toIso(selected) : '', month: selected || earliest || todayUtc() };
    root.__ugbDeliveryState = state;
    if (state.selectedIso) root.dataset.ugbSelectedIso = state.selectedIso;

    const value = root.querySelector('[data-ugb-delivery-value]');
    const hidden = root.querySelector('[data-ugb-delivery-attribute]');
    if (selected) {
      if (value) value.textContent = longDate(selected);
      if (hidden) hidden.value = longDate(selected);
    }

    const trigger = root.querySelector('[data-ugb-delivery-trigger]');
    const calendar = root.querySelector('[data-ugb-calendar]');
    trigger?.addEventListener('click', () => {
      const isOpen = !calendar?.hasAttribute('hidden');
      if (isOpen) {
        calendar?.setAttribute('hidden', '');
        trigger.setAttribute('aria-expanded', 'false');
      } else {
        calendar?.removeAttribute('hidden');
        trigger.setAttribute('aria-expanded', 'true');
        renderCalendar(root, state);
      }
    });

    root.querySelector('[data-ugb-calendar-prev]')?.addEventListener('click', () => {
      state.month = new Date(Date.UTC(state.month.getUTCFullYear(), state.month.getUTCMonth() - 1, 1));
      renderCalendar(root, state);
    });
    root.querySelector('[data-ugb-calendar-next]')?.addEventListener('click', () => {
      state.month = new Date(Date.UTC(state.month.getUTCFullYear(), state.month.getUTCMonth() + 1, 1));
      renderCalendar(root, state);
    });

    renderCalendar(root, state);

    const form = document.getElementById('cart-form');
    if (form && form.dataset.ugbDeliveryBound !== 'true') {
      form.dataset.ugbDeliveryBound = 'true';
      form.addEventListener('submit', (event) => {
        const currentRoot = document.querySelector('[data-ugb-delivery-cart]');
        const currentState = currentRoot?.__ugbDeliveryState;
        const submitter = event.submitter;
        if (submitter?.name === 'checkout' && currentRoot && currentState && !validateCart(currentRoot, currentState)) {
          event.preventDefault();
          event.stopImmediatePropagation();
        }
      }, true);
    }
  }

  function initProduct(root) {
    if (root.dataset.ugbDeliveryInitialized === 'true') return;
    root.dataset.ugbDeliveryInitialized = 'true';
    const config = readConfig(root);
    applyCopy(root, 'product', config);
    const earliest = earliestAvailable(config);
    const label = root.querySelector('[data-ugb-earliest-date]');
    if (label) label.textContent = earliest ? shortDate(earliest) : 'Unavailable';
  }

  function initAll() {
    document.querySelectorAll('[data-ugb-delivery-cart]').forEach(initCart);
    document.querySelectorAll('[data-ugb-delivery-product]').forEach(initProduct);
  }

  document.addEventListener('click', (event) => {
    document.querySelectorAll('[data-ugb-delivery-cart]').forEach((root) => {
      const calendar = root.querySelector('[data-ugb-calendar]');
      if (!calendar || calendar.hasAttribute('hidden')) return;
      if (!root.contains(event.target)) {
        calendar.setAttribute('hidden', '');
        root.querySelector('[data-ugb-delivery-trigger]')?.setAttribute('aria-expanded', 'false');
      }
    });
  });

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;
    document.querySelectorAll('[data-ugb-calendar]:not([hidden])').forEach((calendar) => calendar.setAttribute('hidden', ''));
    document.querySelectorAll('[data-ugb-delivery-trigger]').forEach((trigger) => trigger.setAttribute('aria-expanded', 'false'));
  });

  initAll();
  const observer = new MutationObserver(() => requestAnimationFrame(initAll));
  observer.observe(document.documentElement, { childList: true, subtree: true });
})();
